/**
 * TaskLabelBadge Component
 *
 * Displays a badge for task labels with dynamic coloring.
 * Colors are derived from the label's hex color property.
 *
 * @module components/tasks/TaskLabelBadge
 */

import { memo } from 'react';
import { X } from 'lucide-react';

// ============================================
// Constants
// ============================================

/**
 * Size variants for the badge
 */
const SIZE_CLASSES = {
  sm: 'px-1.5 py-0.5 text-xs',
  md: 'px-2 py-1 text-xs',
  lg: 'px-3 py-1.5 text-sm'
};

/**
 * Default label colors (fallback if no color provided)
 */
const DEFAULT_COLOR = '#6B7280'; // gray-500

// ============================================
// Helper Functions
// ============================================

/**
 * Convert hex color to rgba with alpha
 * @param {string} hex - Hex color code
 * @param {number} alpha - Alpha value (0-1)
 * @returns {string} rgba color string
 */
function hexToRgba(hex, alpha = 1) {
  // Remove # if present
  const cleanHex = hex.replace('#', '');

  // Parse hex values
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);

  // Return rgba string
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ============================================
// Component
// ============================================

/**
 * TaskLabelBadge Component
 *
 * @param {object} props - Component props
 * @param {object} props.label - Label object with name, displayName, color
 * @param {string} props.label.name - Label identifier
 * @param {string} [props.label.displayName] - Label display name (falls back to name)
 * @param {string} [props.label.color] - Hex color for the label
 * @param {string} [props.size='md'] - Badge size: 'sm', 'md', or 'lg'
 * @param {function} [props.onRemove] - Callback when remove button is clicked
 * @param {boolean} [props.clickable=false] - Whether the badge is clickable
 * @param {function} [props.onClick] - Click handler for clickable badges
 * @param {string} [props.className=''] - Additional CSS classes
 * @returns {React.ReactElement} Badge element
 */
const TaskLabelBadge = memo(function TaskLabelBadge({
  label,
  size = 'md',
  onRemove,
  clickable = false,
  onClick,
  className = ''
}) {
  if (!label) {
    console.warn('TaskLabelBadge: label prop is required');
    return null;
  }

  const color = label.color || DEFAULT_COLOR;
  const displayText = label.displayName || label.name;
  const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.md;

  // Dynamic styles based on label color
  const badgeStyle = {
    backgroundColor: hexToRgba(color, 0.15),
    color: color,
    borderColor: hexToRgba(color, 0.3)
  };

  const handleClick = (e) => {
    if (clickable && onClick) {
      e.stopPropagation();
      onClick(label);
    }
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    if (onRemove) {
      onRemove(label.id || label.name);
    }
  };

  return (
    <span
      className={`
        inline-flex items-center gap-1 rounded-full font-medium border
        ${sizeClass}
        ${clickable ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}
        ${className}
      `}
      style={badgeStyle}
      onClick={handleClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      <span>{displayText}</span>
      {onRemove && (
        <button
          onClick={handleRemove}
          className="hover:opacity-70 transition-opacity ml-0.5"
          aria-label={`Remove ${displayText} label`}
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </span>
  );
});

export default TaskLabelBadge;
